import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Header from '../components/Header';
import './GerenciamentoPage.css';
import '../components/Dashboard.css';


const Modal = ({ children, isOpen, onClose, wide = false }) => {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay">
            <div className="modal-content" style={wide ? { maxWidth: '900px', width: '95vw' } : {}}>
                <button onClick={onClose} className="modal-close-btn">&times;</button>
                {children}
            </div>
        </div>
    );
};


// eslint-disable-next-line no-unused-vars
const InformacoesSection = ({ municipio, loading, error, success, onSave, data, onDataChange }) => {
    if (loading && !municipio) return <p>Carregando dados...</p>;

    return (
        <>
            <h2>Informações do Município</h2>
            <p>Defina e visualize o total de imóveis e a meta de visitas anuais para o seu município.</p>
            {error && <p className="error-message">{error}</p>}
            {municipio && (
                <form onSubmit={onSave} className="gerenciamento-form">
                    <div className="form-group">
                        <label htmlFor="municipio-nome">Município</label>
                        <input id="municipio-nome" type="text" value={municipio.nome} disabled />
                    </div>
                    <div className="form-group">
                        <label htmlFor="total-imoveis">Total de Imóveis Existentes</label>
                        <input
                            id="total-imoveis"
                            type="number"
                            value={data.totalImoveis}
                            onChange={(e) => onDataChange('totalImoveis', e.target.value)}
                            placeholder="Ex: 15000"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="meta-visitas">Meta de Imóveis a Serem Visitados no Ano</label>
                        <input
                            id="meta-visitas"
                            type="number"
                            value={data.metaVisitas}
                            onChange={(e) => onDataChange('metaVisitas', e.target.value)}
                            placeholder="Ex: 12000"
                        />
                    </div>
                    <div className="form-actions">
                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? 'Salvando...' : 'Salvar Alterações'}
                        </button>
                    </div>
                    {success && <p className="success-message">{success}</p>}
                </form>
            )}
        </>
    );
};



const AgentesSection = ({ municipio }) => {
    const [agentes, setAgentes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [addFormState, setAddFormState] = useState({ nome: '', login: '', password: '', cargo: '' });

    const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const [editingLogin, setEditingLogin] = useState('');
    const [newPassword, setNewPassword] = useState('');

    const [uploadingId, setUploadingId] = useState(null);
    const avatarInputRef = React.useRef(null);
    const uploadTargetRef = React.useRef(null);

    const handleAvatarClick = (agente) => {
        uploadTargetRef.current = agente;
        avatarInputRef.current?.click();
    };

    const handleAvatarFileChange = async (e) => {
        const file = e.target.files?.[0];
        const agente = uploadTargetRef.current;
        if (!file || !agente) return;

        const ext = file.name.split('.').pop();
        const path = `agentes/${agente.id}/avatar.${ext}`;

        setUploadingId(agente.id);
        try {
            const { error: uploadError } = await supabase.storage.from('profile_pictures').upload(path, file, { upsert: true });
            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage.from('profile_pictures').getPublicUrl(path);
            const { error: updateError } = await supabase.from('agentes').update({ avatar_url: publicUrl }).eq('id', agente.id);
            if (updateError) throw updateError;

            fetchAgentes();
        } catch (err) {
            alert('Erro ao enviar foto. Verifique se o bucket "profile_pictures" existe e é público.');
        } finally {
            setUploadingId(null);
            e.target.value = '';
        }
    };

    const fetchAgentes = useCallback(async () => {
        if (!municipio) return;
        setLoading(true);
        setError('');
        try {
            const { data, error } = await supabase
                .from('agentes')
                .select('id, user_id, nome, email, ativo, avatar_url, created_at, registro_matricula, cargo')
                .eq('municipio_id', municipio.id)
                .order('nome', { ascending: true });
            if (error) throw error;

            setAgentes(data || []);
        } catch (err) {
            setError('Falha ao buscar agentes.');
        } finally {
            setLoading(false);
        }
    }, [municipio]);

    useEffect(() => { fetchAgentes(); }, [fetchAgentes]);

    const handleAddFormChange = (field, value) => {
        setAddFormState(prev => ({ ...prev, [field]: value }));
    };

    const handleAddAgente = async (e) => {
        e.preventDefault();
        if (!addFormState.nome || !addFormState.login || !addFormState.password) {
            alert('Todos os campos são obrigatórios para criar um agente.');
            return;
        }

        if (addFormState.password.length < 6) {
            alert('A senha inicial deve ter no mínimo 6 caracteres.');
            return;
        }

        setLoading(true);
        setError('');

        const loginInput = addFormState.login.trim();
        let loginPart, domainPart;

        if (loginInput.includes('@')) {
            const parts = loginInput.split('@');
            if (parts.length !== 2 || !parts[0] || !parts[1]) {
                setError('Formato de e-mail inválido.');
                setLoading(false);
                return;
            }
            [loginPart, domainPart] = parts;
        } else {
            loginPart = loginInput;
            domainPart = 'dominio.interno';
        }

        try {
            const { error: functionError } = await supabase.functions.invoke('create-agent', {
                body: {
                    nome: addFormState.nome,
                    login: loginPart,
                    domain: domainPart,
                    password: addFormState.password,
                    municipio_id: municipio.id,
                },
            });

            if (functionError) throw functionError;

            if (addFormState.cargo) {
                const emailCriado = `${loginPart}@${domainPart}`;
                await supabase.from('agentes').update({ cargo: addFormState.cargo }).eq('municipio_id', municipio.id).eq('email', emailCriado);
            }

            alert('Agente criado com sucesso!');
            setAddFormState({ nome: '', login: '', password: '', cargo: '' });
            fetchAgentes();

        } catch (err) {
            let errorMessage = err.message;
            try {
                const errorBody = await err.context.json();
                errorMessage = errorBody.error || errorMessage;
            } catch { /* Ignora */ }
            setError(`Erro ao criar agente: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAgenteStatus = async (agenteId, currentStatus) => {
        const actionText = currentStatus ? 'desativar' : 'reativar';
        if (!window.confirm(`Tem certeza que deseja ${actionText} este agente?`)) return;
        setLoading(true);
        try {
            const { error } = await supabase.from('agentes').update({ ativo: !currentStatus }).eq('id', agenteId);
            if (error) throw error;
            fetchAgentes();
        } catch (err) {
            setError(`Falha ao ${actionText} o agente.`);
        } finally {
            setLoading(false);
        }
    };

    const getDisplayLogin = (email) => {
        if (!email) return '';
        const parts = email.split('@');
        if (parts.length === 2 && parts[1] === 'dominio.interno') {
            return parts[0];
        }
        return email;
    };

    const openEditAgentModal = (agente) => {
        setEditingAgent(agente);
        setNewPassword('');
        setEditingLogin(getDisplayLogin(agente.email));
        setIsAgentModalOpen(true);
    };

    const handleUpdateAgent = async (e) => {
        e.preventDefault();


        if (newPassword && newPassword.length > 0 && newPassword.length < 6) {
            alert('A nova senha deve ter no mínimo 6 caracteres.');
            return;
        }

        setModalLoading(true);

        const loginInput = editingLogin.trim();
        let loginPart, domainPart;

        if (loginInput.includes('@')) {
            const parts = loginInput.split('@');
            if (parts.length !== 2 || !parts[0] || !parts[1]) {
                alert('Formato de e-mail inválido.');
                setModalLoading(false);
                return;
            }
            [loginPart, domainPart] = parts;
        } else {
            loginPart = loginInput;
            domainPart = 'dominio.interno';
        }

        try {
            const { error: functionError } = await supabase.functions.invoke('update-agent', {
                body: {
                    user_id: editingAgent.user_id,
                    nome: editingAgent.nome,
                    login: loginPart,
                    domain: domainPart,
                    password: newPassword || undefined
                }
            });

            if (functionError) throw functionError;

            await supabase.from('agentes').update({ cargo: editingAgent.cargo || null }).eq('id', editingAgent.id);

            setIsAgentModalOpen(false);
            setEditingAgent(null);
            fetchAgentes();
            alert('Agente atualizado com sucesso!');
        } catch (err) {
            let errorMessage = err.message;
            try {
                const errorBody = await err.context.json();
                errorMessage = errorBody.error || errorMessage;
            } catch { /* Ignora */ }
            alert(`Erro ao atualizar o agente: ${errorMessage}`);
        } finally {
            setModalLoading(false);
        }
    };

    return (
        <>
            <h2>Gerenciamento da Equipe</h2>

            <form onSubmit={handleAddAgente} className="gerenciamento-form subsection-form" autoComplete="off">
                <input type="text" style={{ display: 'none' }} />
                <input type="password" style={{ display: 'none' }} />

                <div className="form-row">
                    <div className="form-group-flex">
                        <label htmlFor="agente-nome">Nome Completo</label>
                        <input
                            id="agente-nome"
                            type="text"
                            value={addFormState.nome}
                            onChange={(e) => handleAddFormChange('nome', e.target.value)}
                            placeholder="Nome do Agente"
                            required
                            autoComplete="off"
                        />
                    </div>
                    <div className="form-group-flex">
                        <label htmlFor="agente-login">Login</label>
                        <input
                            id="agente-login"
                            type="text"
                            value={addFormState.login}
                            onChange={(e) => handleAddFormChange('login', e.target.value)}
                            placeholder="CPF, número ou e-mail"
                            required
                            autoComplete="off"
                        />
                    </div>
                    <div className="form-group-flex">
                        <label htmlFor="agente-password">Senha Inicial</label>
                        <input
                            id="agente-password"
                            type="password"
                            value={addFormState.password}
                            onChange={(e) => handleAddFormChange('password', e.target.value)}
                            placeholder="Defina uma senha"
                            required
                            autoComplete="new-password"
                        />
                    </div>
                    <div className="form-group-flex">
                        <label htmlFor="agente-cargo">Cargo / Função</label>
                        <input
                            id="agente-cargo"
                            type="text"
                            value={addFormState.cargo}
                            onChange={(e) => handleAddFormChange('cargo', e.target.value)}
                            placeholder="Ex: Agente de Combate às Endemias"
                            autoComplete="off"
                        />
                    </div>
                </div>
                <div className="form-actions">
                    <button type="submit" className="submit-btn-secondary" disabled={loading}>{loading ? 'Criando...' : 'Adicionar Agente'}</button>
                </div>
            </form>

            {error && <p className="error-message">{error}</p>}

            <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarFileChange}
            />

            <div className="agentes-table-wrap">
                <table className="agentes-table">
                    <thead>
                        <tr>
                            <th style={{ width: 44 }}></th>
                            <th style={{ width: 90 }}>ID</th>
                            <th>Nome</th>
                            <th>Login</th>
                            <th>Status</th>
                            <th>Cadastrado em</th>
                            <th>Cargo / Função</th>
                            <th className="th-center">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={7} className="agentes-empty">Carregando...</td></tr>
                        )}
                        {!loading && agentes.length === 0 && (
                            <tr><td colSpan={7} className="agentes-empty">Nenhum agente cadastrado.</td></tr>
                        )}
                        {agentes.map(agente => {
                            const dataRegistro = agente.created_at
                                ? new Date(agente.created_at).toLocaleDateString('pt-BR')
                                : '—';
                            return (
                                <tr key={agente.id} className={!agente.ativo ? 'row-inactive' : ''}>
                                    <td>
                                        <div
                                            className="agente-avatar-wrap"
                                            title="Clique para adicionar/trocar foto"
                                            onClick={() => handleAvatarClick(agente)}
                                        >
                                            {uploadingId === agente.id
                                                ? <div className="agente-avatar-initials">...</div>
                                                : agente.avatar_url
                                                    ? <img src={agente.avatar_url} alt={agente.nome} className="agente-avatar" />
                                                    : <div className="agente-avatar-initials">Sem<br/>foto</div>
                                            }
                                            <div className="agente-avatar-overlay">&#128247;</div>
                                        </div>
                                    </td>
                                    <td className="agente-id" title="Clique para copiar o ID completo" onClick={() => { navigator.clipboard.writeText(agente.id); }} style={{ cursor: 'pointer' }}>{agente.id.slice(0, 8)}…</td>
                                    <td>
                                        <div className="agente-nome">{agente.nome}</div>
                                        {agente.registro_matricula && <div className="agente-matricula">#{agente.registro_matricula}</div>}
                                    </td>
                                    <td className="agente-email">{getDisplayLogin(agente.email)}</td>
                                    <td>
                                        <span className={agente.ativo ? 'badge-ativo' : 'badge-inativo'}>
                                            {agente.ativo ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </td>
                                    <td className="agente-data">{dataRegistro}</td>
                                    <td className="agente-cargo" title={agente.cargo || ''}>{agente.cargo || '—'}</td>
                                    <td className="td-center">
                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                            <button className="btn-action btn-edit" onClick={() => openEditAgentModal(agente)}>Editar</button>
                                            <button
                                                className={`btn-action ${agente.ativo ? 'btn-delete' : 'btn-reactivate'}`}
                                                onClick={() => handleToggleAgenteStatus(agente.id, agente.ativo)}
                                            >
                                                {agente.ativo ? 'Desativar' : 'Reativar'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Modal isOpen={isAgentModalOpen} onClose={() => setIsAgentModalOpen(false)}>
                {editingAgent && (
                    <form onSubmit={handleUpdateAgent} className="gerenciamento-form" autoComplete="off">
                        <input type="text" style={{ display: 'none' }} />
                        <input type="password" style={{ display: 'none' }} />

                        <h3>Editando Agente</h3>
                        <div className="form-group">
                            <label>Nome do Agente</label>
                            <input
                                type="text"
                                value={editingAgent.nome}
                                onChange={(e) => setEditingAgent({ ...editingAgent, nome: e.target.value })}
                                placeholder="Nome completo do agente"
                                required
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group">
                            <label>Login</label>
                            <input
                                type="text"
                                value={editingLogin}
                                onChange={(e) => setEditingLogin(e.target.value)}
                                placeholder="CPF, número ou e-mail"
                                required
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group">
                            <label>Cargo / Função</label>
                            <input
                                type="text"
                                value={editingAgent.cargo || ''}
                                onChange={(e) => setEditingAgent({ ...editingAgent, cargo: e.target.value })}
                                placeholder="Ex: Coordenador da Vigilância Sanitária"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group">
                            <label>Nova Senha (opcional)</label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Deixe em branco para manter a atual"
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-actions">
                            <button type="submit" className="submit-btn" disabled={modalLoading}>{modalLoading ? 'Salvando...' : 'Salvar Alterações'}</button>
                        </div>
                    </form>
                )}
            </Modal>
        </>
    );
};


const RG03_DEFAULTS = {
    tipo_registro: 'inclusao',
    codigo_uf: '',
    codigo_municipio_ibge: '',
    data_rg: '',
    data_atualizacao: '',
    status_localidade: '1',
    qtd_imoveis_residenciais: '',
    qtd_imoveis_comerciais: '',
    qtd_outros_imoveis: '',
    qtd_habitantes: '',
    qtd_quarteiroes: '',
    qtd_pontos_estrategicos: '',
    qtd_armadilhas_instaladas: '',
    qtd_terrenos_baldios: '',
    infra_energia_eletrica: '',
    infra_agua_encanada: '',
    infra_tratamento_esgoto: '',
    infra_lavanderia_coletiva: '',
    infra_casas_privada: '',
    infra_coleta_lixo: '',
    infra_rede_telefonica: '',
    infra_transporte_publico: '',
    infra_rua_pavimentada: '',
    infra_escola: '',
    infra_posto_saude: '',
    infra_acesso_permanente: '',
    infra_pacs_psf: '',
    ocor_animais_cachorros: '',
    ocor_animais_gatos: '',
    ocor_animais_roedores: '',
    ocor_malaria: '',
    ocor_dengue: '',
    ocor_esquistossomose: '',
    ocor_leishmaniose: '',
    ocor_febre_maculosa: '',
    ocor_peste: '',
    ocor_doenca_chagas: '',
    ocor_febre_amarela: '',
    longitude: '',
    latitude: '',
    altitude_m: '',
    distancia_centro_km: '',
    visto_supervisor: '',
    data_visto: '',
    classificacao: '',
};

const RG03_CAMPOS_UPDATE = [
    'nome', 'codigo', 'categoria', 'classificacao',
    'tipo_registro', 'codigo_uf', 'codigo_municipio_ibge',
    'data_rg', 'data_atualizacao', 'status_localidade',
    'qtd_imoveis_residenciais', 'qtd_imoveis_comerciais', 'qtd_outros_imoveis',
    'qtd_habitantes', 'qtd_quarteiroes', 'qtd_pontos_estrategicos',
    'qtd_armadilhas_instaladas', 'qtd_terrenos_baldios',
    'infra_energia_eletrica', 'infra_agua_encanada', 'infra_tratamento_esgoto',
    'infra_lavanderia_coletiva', 'infra_casas_privada', 'infra_coleta_lixo',
    'infra_rede_telefonica', 'infra_transporte_publico', 'infra_rua_pavimentada',
    'infra_escola', 'infra_posto_saude', 'infra_acesso_permanente', 'infra_pacs_psf',
    'ocor_animais_cachorros', 'ocor_animais_gatos', 'ocor_animais_roedores',
    'ocor_malaria', 'ocor_dengue', 'ocor_esquistossomose', 'ocor_leishmaniose',
    'ocor_febre_maculosa', 'ocor_peste', 'ocor_doenca_chagas', 'ocor_febre_amarela',
    'longitude', 'latitude', 'altitude_m', 'distancia_centro_km',
    'visto_supervisor', 'data_visto',
];

const sRG03 = {
    table: { borderCollapse: 'collapse', width: '100%', fontSize: '12px', marginBottom: '0' },
    td: { border: '1px solid #555', padding: '4px 6px', verticalAlign: 'middle' },
    th: { border: '1px solid #555', padding: '4px 6px', background: '#e8e8e8', fontWeight: '700', textAlign: 'center', fontSize: '11px' },
    label: { fontSize: '11px', color: '#444', display: 'block', marginBottom: '2px' },
    input: { border: '1px solid #aaa', borderRadius: '3px', padding: '3px 6px', fontSize: '12px', width: '100%', boxSizing: 'border-box' },
    inputNum: { border: '1px solid #aaa', borderRadius: '3px', padding: '3px 4px', fontSize: '12px', width: '100%', boxSizing: 'border-box' },
    sectionHeader: { background: '#d0d0d0', fontWeight: '700', textAlign: 'center', fontSize: '11px', border: '1px solid #555', padding: '4px' },
    checkRow: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' },
};

const FORM_VAZIO = { ...RG03_DEFAULTS, nome: '', codigo: '', categoria: '' };

/* ── Formulário RG-03 reutilizável ── */
const RG03FormFields = ({ form, set, formPrefix }) => {
    const n = (field, value) => set(field, value === '' ? '' : Number(value));

    /* Campo S/N/I — pequena caixa de texto de 1 caractere */
    const SNI = ({ field, label }) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', padding: '1px 0' }}>
            <span style={{ fontSize: '12px', flex: 1 }}>{label}</span>
            <input
                type="text"
                maxLength={1}
                value={form[field] || ''}
                onChange={e => set(field, e.target.value.toUpperCase())}
                style={{ width: '28px', height: '22px', textAlign: 'center', border: '1px solid #555', borderRadius: '2px', fontSize: '13px', fontWeight: '700', padding: '0', flexShrink: 0 }}
                placeholder=" "
            />
        </div>
    );
    const Num = ({ field }) => (
        <input type="number" min="0" style={sRG03.inputNum} value={form[field] ?? ''} onChange={e => n(field, e.target.value)} />
    );

    return (
        <table style={sRG03.table}>
            <tbody>
                {/* INCLUSÃO / ALTERAÇÃO */}
                <tr>
                    <td style={{ ...sRG03.td, width: '50%', textAlign: 'center' }}>
                        <label style={{ ...sRG03.checkRow, justifyContent: 'center' }}>
                            <input type="radio" name={`${formPrefix}_tipo`} value="inclusao" checked={form.tipo_registro === 'inclusao'} onChange={e => set('tipo_registro', e.target.value)} />
                            <strong>INCLUSÃO</strong>
                        </label>
                    </td>
                    <td style={{ ...sRG03.td, width: '50%', textAlign: 'center' }}>
                        <label style={{ ...sRG03.checkRow, justifyContent: 'center' }}>
                            <input type="radio" name={`${formPrefix}_tipo`} value="alteracao" checked={form.tipo_registro === 'alteracao'} onChange={e => set('tipo_registro', e.target.value)} />
                            <strong>ALTERAÇÃO</strong>
                        </label>
                    </td>
                </tr>

                {/* UF / Município */}
                <tr>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Código e nome UF (IBGE)</span>
                        <input style={sRG03.input} value={form.codigo_uf || ''} onChange={e => set('codigo_uf', e.target.value)} />
                    </td>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Código e nome do município (IBGE)</span>
                        <input style={sRG03.input} value={form.codigo_municipio_ibge || ''} onChange={e => set('codigo_municipio_ibge', e.target.value)} />
                    </td>
                </tr>

                {/* Cód. / Nome */}
                <tr>
                    <td style={{ ...sRG03.td, width: '25%' }}>
                        <span style={sRG03.label}>Cód. localidade</span>
                        <input style={sRG03.input} value={form.codigo || ''} onChange={e => set('codigo', e.target.value)} />
                    </td>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Nome da localidade *</span>
                        <input style={sRG03.input} value={form.nome || ''} onChange={e => set('nome', e.target.value)} required />
                    </td>
                </tr>

                {/* Categoria */}
                <tr>
                    <td style={sRG03.td} colSpan={2}>
                        <span style={sRG03.label}>Categoria da localidade</span>
                        <input style={sRG03.input} value={form.categoria || ''} onChange={e => set('categoria', e.target.value)} />
                    </td>
                </tr>

                {/* Datas */}
                <tr>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Data de Reconhecimento</span>
                        <input type="date" style={sRG03.input} value={form.data_rg || ''} onChange={e => set('data_rg', e.target.value)} />
                    </td>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Data da atualização</span>
                        <input type="date" style={sRG03.input} value={form.data_atualizacao || ''} onChange={e => set('data_atualizacao', e.target.value)} />
                    </td>
                </tr>

                {/* Status / Classificação */}
                <tr>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Status da localidade</span>
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <label style={sRG03.checkRow}><input type="radio" name={`${formPrefix}_status`} value="1" checked={form.status_localidade === '1'} onChange={e => set('status_localidade', e.target.value)} /> 1-Ativa</label>
                            <label style={sRG03.checkRow}><input type="radio" name={`${formPrefix}_status`} value="2" checked={form.status_localidade === '2'} onChange={e => set('status_localidade', e.target.value)} /> 2-Extinta</label>
                        </div>
                    </td>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Classificação da localidade</span>
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <label style={sRG03.checkRow}><input type="radio" name={`${formPrefix}_classif`} value="Urbana" checked={form.classificacao === 'Urbana'} onChange={e => set('classificacao', e.target.value)} /> U-Urbana</label>
                            <label style={sRG03.checkRow}><input type="radio" name={`${formPrefix}_classif`} value="Rural" checked={form.classificacao === 'Rural'} onChange={e => set('classificacao', e.target.value)} /> R-Rural</label>
                        </div>
                    </td>
                </tr>

                {/* Quantidades imóveis */}
                <tr>
                    <td style={sRG03.td}><span style={sRG03.label}>Quantidade de imóveis residenciais</span><Num field="qtd_imoveis_residenciais" /></td>
                    <td style={sRG03.td}><span style={sRG03.label}>Quantidade de imóveis comerciais</span><Num field="qtd_imoveis_comerciais" /></td>
                </tr>
                <tr>
                    <td style={sRG03.td}><span style={sRG03.label}>Quantidade de outros tipos imóveis</span><Num field="qtd_outros_imoveis" /></td>
                    <td style={sRG03.td}><span style={sRG03.label}>Quantidade de habitantes</span><Num field="qtd_habitantes" /></td>
                </tr>
                <tr>
                    <td style={sRG03.td}><span style={sRG03.label}>Quantidade de quarteirões</span><Num field="qtd_quarteiroes" /></td>
                    <td style={sRG03.td}><span style={sRG03.label}>Quantidade de pontos estratégicos</span><Num field="qtd_pontos_estrategicos" /></td>
                </tr>
                <tr>
                    <td style={sRG03.td}><span style={sRG03.label}>Quantidade de armadilhas instaladas</span><Num field="qtd_armadilhas_instaladas" /></td>
                    <td style={sRG03.td}><span style={sRG03.label}>Quantidade de terrenos baldios</span><Num field="qtd_terrenos_baldios" /></td>
                </tr>

                {/* Infraestrutura */}
                <tr><td colSpan={2} style={sRG03.sectionHeader}>Infra-estrutura existente na localidade (S-sim  N-não  I-ignorado)</td></tr>
                <tr><td style={sRG03.td}><SNI field="infra_energia_eletrica" label="Energia elétrica" /></td><td style={sRG03.td}><SNI field="infra_agua_encanada" label="Água encanada" /></td></tr>
                <tr><td style={sRG03.td}><SNI field="infra_tratamento_esgoto" label="Tratamento de esgoto" /></td><td style={sRG03.td}><SNI field="infra_lavanderia_coletiva" label="Lavanderia coletiva" /></td></tr>
                <tr><td style={sRG03.td}><SNI field="infra_casas_privada" label="Casas com privada" /></td><td style={sRG03.td}><SNI field="infra_coleta_lixo" label="Coleta de lixo" /></td></tr>
                <tr><td style={sRG03.td}><SNI field="infra_rede_telefonica" label="Rede telefônica" /></td><td style={sRG03.td}><SNI field="infra_transporte_publico" label="Transporte público" /></td></tr>
                <tr><td style={sRG03.td}><SNI field="infra_rua_pavimentada" label="Rua pavimentada" /></td><td style={sRG03.td}><SNI field="infra_escola" label="Escola" /></td></tr>
                <tr><td style={sRG03.td}><SNI field="infra_posto_saude" label="Posto de saúde" /></td><td style={sRG03.td}><SNI field="infra_acesso_permanente" label="Acesso permanente" /></td></tr>
                <tr><td style={sRG03.td} colSpan={2}><SNI field="infra_pacs_psf" label="PACS / PSF" /></td></tr>

                {/* Ocorrência de animais */}
                <tr><td colSpan={2} style={sRG03.sectionHeader}>Ocorrência de animais (S-sim  N-não  I-ignorado)</td></tr>
                <tr>
                    <td style={sRG03.td}><SNI field="ocor_animais_cachorros" label="Cachorros" /></td>
                    <td style={sRG03.td}><SNI field="ocor_animais_gatos" label="Gatos" /></td>
                </tr>
                <tr>
                    <td style={sRG03.td} colSpan={2}><SNI field="ocor_animais_roedores" label="Roedores" /></td>
                </tr>

                {/* Ocorrências de doenças */}
                <tr><td colSpan={2} style={sRG03.sectionHeader}>Ocorrência ou risco de ocorrência (S-sim  N-não  I-ignorado)</td></tr>
                <tr><td style={sRG03.td}><SNI field="ocor_malaria" label="Malária" /></td><td style={sRG03.td}><SNI field="ocor_dengue" label="Dengue" /></td></tr>
                <tr><td style={sRG03.td}><SNI field="ocor_esquistossomose" label="Esquistossomose" /></td><td style={sRG03.td}><SNI field="ocor_leishmaniose" label="Leishmaniose" /></td></tr>
                <tr><td style={sRG03.td}><SNI field="ocor_febre_maculosa" label="Febre Maculosa" /></td><td style={sRG03.td}><SNI field="ocor_peste" label="Peste" /></td></tr>
                <tr><td style={sRG03.td}><SNI field="ocor_doenca_chagas" label="Doença de Chagas" /></td><td style={sRG03.td}><SNI field="ocor_febre_amarela" label="Febre Amarela" /></td></tr>

                {/* Dados geográficos */}
                <tr><td colSpan={2} style={sRG03.sectionHeader}>Dados geográficos (utilizar datum WGS84 e sistema de coordenadas em deg (hddd,dddddd))</td></tr>
                <tr>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Longitude</span>
                        <input type="number" step="any" style={sRG03.input} value={form.longitude ?? ''} onChange={e => set('longitude', e.target.value)} />
                    </td>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Latitude</span>
                        <input type="number" step="any" style={sRG03.input} value={form.latitude ?? ''} onChange={e => set('latitude', e.target.value)} />
                    </td>
                </tr>
                <tr>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Altitude (m)</span>
                        <input type="number" step="any" style={sRG03.input} value={form.altitude_m ?? ''} onChange={e => set('altitude_m', e.target.value)} />
                    </td>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Distância do centro (Km)</span>
                        <input type="number" step="any" style={sRG03.input} value={form.distancia_centro_km ?? ''} onChange={e => set('distancia_centro_km', e.target.value)} />
                    </td>
                </tr>

                {/* Visto supervisor */}
                <tr>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Visto do Supervisor</span>
                        <input style={sRG03.input} value={form.visto_supervisor || ''} onChange={e => set('visto_supervisor', e.target.value)} placeholder="Nome do supervisor" />
                    </td>
                    <td style={sRG03.td}>
                        <span style={sRG03.label}>Data do visto</span>
                        <input type="date" style={sRG03.input} value={form.data_visto || ''} onChange={e => set('data_visto', e.target.value)} />
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

/* ── Cabeçalho SISLOC ── */
const RG03Header = () => (
    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <div style={{ fontWeight: '800', fontSize: '14px', letterSpacing: '0.5px' }}>REFERENCIAL GEOGRÁFICO</div>
        <div style={{ fontWeight: '700', fontSize: '13px' }}>Resumo de Localidade</div>
    </div>
);

const buildPayload = (form) => {
    const payload = {};
    RG03_CAMPOS_UPDATE.forEach(f => {
        payload[f] = form[f] === '' ? null : form[f];
    });
    payload.codigo = payload.codigo || null;
    return payload;
};

/* ── Seção principal ── */
// eslint-disable-next-line no-unused-vars
const LocalidadesSection = ({ municipio }) => {
    const [localidades, setLocalidades] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [addLoading, setAddLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [error, setError] = useState('');

    /* Formulário de INCLUSÃO (sempre visível) */
    const [addForm, setAddFormState] = useState({ ...FORM_VAZIO, tipo_registro: 'inclusao' });
    const setAdd = (field, value) => setAddFormState(prev => ({ ...prev, [field]: value }));

    /* Formulário de ALTERAÇÃO (colapsável, abaixo da lista) */
    const [editForm, setEditFormState] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const setEdit = (field, value) => setEditFormState(prev => ({ ...prev, [field]: value }));
    const editRef = React.useRef(null);

    const fetchLocalidades = useCallback(async () => {
        if (!municipio) return;
        setListLoading(true);
        try {
            const { data, error } = await supabase.from('localidades').select('*').eq('municipio_id', municipio.id).order('nome', { ascending: true });
            if (error) throw error;
            setLocalidades(data || []);
        } catch (err) {
            setError('Falha ao buscar localidades.');
        } finally {
            setListLoading(false);
        }
    }, [municipio]);

    useEffect(() => { fetchLocalidades(); }, [fetchLocalidades]);

    const handleIncluir = async (e) => {
        e.preventDefault();
        if (!addForm.nome) { alert('O nome da localidade é obrigatório.'); return; }
        setAddLoading(true);
        try {
            const { error } = await supabase.from('localidades').insert({ ...buildPayload(addForm), municipio_id: municipio.id });
            if (error) throw error;
            setAddFormState({ ...FORM_VAZIO, tipo_registro: 'inclusao' });
            fetchLocalidades();
            alert('Localidade cadastrada com sucesso!');
        } catch (err) {
            alert('Erro ao cadastrar localidade.');
        } finally {
            setAddLoading(false);
        }
    };

    const handleAbrirEdicao = (loc) => {
        setEditFormState({ ...FORM_VAZIO, ...loc, tipo_registro: 'alteracao' });
        setEditOpen(true);
        setTimeout(() => {
            editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
    };

    const handleSalvarEdicao = async (e) => {
        e.preventDefault();
        if (!editForm?.nome) { alert('O nome da localidade é obrigatório.'); return; }
        setEditLoading(true);
        try {
            const { error } = await supabase.from('localidades').update(buildPayload(editForm)).eq('id', editForm.id);
            if (error) throw error;
            fetchLocalidades();
            alert('Localidade atualizada com sucesso!');
        } catch (err) {
            alert('Erro ao atualizar localidade.');
        } finally {
            setEditLoading(false);
        }
    };

    const handleExcluir = async (localidadeId) => {
        if (!window.confirm('Tem certeza que deseja excluir esta localidade? Esta ação não pode ser desfeita.')) return;
        try {
            const { error } = await supabase.from('localidades').delete().eq('id', localidadeId);
            if (error) throw error;
            if (editForm?.id === localidadeId) { setEditFormState(null); setEditOpen(false); }
            fetchLocalidades();
        } catch (err) {
            alert('Erro ao excluir localidade. Verifique se ela não está sendo usada por registros.');
        }
    };

    return (
        <>
            <h2>Gerenciamento de Localidades</h2>
            {error && <p className="error-message">{error}</p>}

            {/* ── FORM INCLUSÃO (sempre visível) ── */}
            <form onSubmit={handleIncluir} style={{ marginBottom: '32px' }}>
                <RG03Header />
                <RG03FormFields form={addForm} set={setAdd} formPrefix="add" />
                <div className="form-actions" style={{ marginTop: '12px' }}>
                    <button type="submit" className="submit-btn" disabled={addLoading}>
                        {addLoading ? 'Salvando...' : 'Cadastrar Localidade'}
                    </button>
                </div>
            </form>

            {/* ── LISTA ── */}
            <div style={{ marginTop: '40px', borderTop: '2px solid #e2e8f0', paddingTop: '20px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '10px' }}>Localidades Cadastradas</h3>
                {listLoading && <p style={{ fontSize: '13px', color: '#64748b' }}>Carregando...</p>}
                {!listLoading && localidades.length === 0 && <p style={{ fontSize: '13px', color: '#64748b' }}>Nenhuma localidade cadastrada.</p>}
                {localidades.map(loc => (
                    <div key={loc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderBottom: '1px solid #f1f5f9', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', flex: 1 }}>{loc.nome}</span>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <button className="btn-action btn-edit" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={() => handleAbrirEdicao(loc)}>Visualizar / Alterar</button>
                            <button className="btn-action btn-delete" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={() => handleExcluir(loc.id)}>Excluir</button>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── FORM ALTERAÇÃO (colapsável, abaixo da lista) ── */}
            {editForm && (
                <div ref={editRef} style={{ marginTop: '24px', border: '1px solid #d1d5db', borderRadius: '10px', overflow: 'hidden' }}>
                    {/* Cabeçalho colapsável */}
                    <div
                        onClick={() => setEditOpen(o => !o)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f1f5f9', cursor: 'pointer', userSelect: 'none' }}
                    >
                        <span style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>
                            Visualizar / Alterar: <span style={{ color: '#2563eb' }}>{editForm.nome}</span>
                        </span>
                        <span style={{ fontSize: '18px', color: '#64748b', transform: editOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
                    </div>

                    {/* Conteúdo colapsável */}
                    {editOpen && (
                        <form onSubmit={handleSalvarEdicao} style={{ padding: '16px' }}>
                            <RG03Header />
                            <RG03FormFields form={editForm} set={setEdit} formPrefix="edit" />
                            <div className="form-actions" style={{ marginTop: '12px' }}>
                                <button type="button" onClick={() => setEditOpen(false)} style={{ marginRight: '10px', padding: '8px 16px', background: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                                    Recolher
                                </button>
                                <button type="submit" className="submit-btn" disabled={editLoading}>
                                    {editLoading ? 'Salvando...' : 'Salvar Alterações'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}
        </>
    );
};


const GerenciamentoPage = () => {
    const [municipio, setMunicipio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [informacoesData, setInformacoesData] = useState({ totalImoveis: '', metaVisitas: '' });

    const fetchMunicipioData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Usuário não autenticado.");

            const { data: profile, error: profileError } = await supabase.from('profiles').select('municipio_id').eq('id', user.id).single();
            if (profileError || !profile || !profile.municipio_id) throw new Error("Perfil de administrador não encontrado ou não associado a um município.");

            const { data, error: fetchError } = await supabase.from('municipios').select('*').eq('id', profile.municipio_id).single();
            if (fetchError) throw fetchError;

            if (data) {
                setMunicipio(data);
                setInformacoesData({ totalImoveis: data.total_imoveis || '', metaVisitas: data.meta_visitas_anuais || '' });
            } else {
                throw new Error("Município não encontrado.");
            }
        } catch (err) {
            setError(`Erro ao carregar dados: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMunicipioData(); }, [fetchMunicipioData]);

    // eslint-disable-next-line no-unused-vars
    const handleInformacoesChange = (field, value) => {
        setInformacoesData(prev => ({ ...prev, [field]: value }));
    };

    // eslint-disable-next-line no-unused-vars
    const handleSaveInformacoes = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const { error: updateError } = await supabase.from('municipios').update({
                total_imoveis: informacoesData.totalImoveis ? parseInt(informacoesData.totalImoveis, 10) : null,
                meta_visitas_anuais: informacoesData.metaVisitas ? parseInt(informacoesData.metaVisitas, 10) : null
            }).eq('id', municipio.id);

            if (updateError) throw updateError;
        } catch (err) {
            setError('Erro ao salvar as alterações. Verifique os valores inseridos.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="portal-layout">
            <Header activeView="gerenciamento" />
            <main className="main-content">
                <div className="content-container">
                    <div className="gerenciamento-container">
                        <div className="gerenciamento-header">
                            <h1>Equipe de Vigilância em Saúde</h1>
                            <p>Gerencie os logins de acesso ao painel e ao aplicativo.</p>
                        </div>

                        {loading && !municipio && <div className="gerenciamento-section"><p>Carregando...</p></div>}

                        {!loading && error && !municipio && <div className="gerenciamento-section"><p className="error-message">{error}</p></div>}

                        {municipio && (
                            <div className="gerenciamento-section">
                                <AgentesSection municipio={municipio} />
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default GerenciamentoPage;
